import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { theme, GlobalStyle } from './Theme';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import Footer from './components/Footer';
import AuthModal from './components/AuthModal';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PaymentProvider } from './contexts/PaymentContext';

// Pages
import HomePage from './pages/HomePage';
import ShippingDeliveryPage from './pages/ShippingDeliveryPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import CancellationRefundPage from './pages/CancellationRefundPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import AdminFlowerPage from './pages/AdminFlowerPage';

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 5,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
  exit: {
    opacity: 0,
    y: -5,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
};

// Protected route component with auth modal
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // If not loading and no user, show auth modal
    if (!loading && !currentUser) {
      setShowAuthModal(true);
    }
  }, [loading, currentUser]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-light">Loading...</p>
        </motion.div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            navigate('/');
          }}
        />
        <Navigate to="/" />
      </>
    );
  }

  return children;
};

// Admin route component
const AdminRoute = ({ children }) => {
  const { currentUser, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-light">Loading...</p>
        </motion.div>
      </div>
    );
  }

  if (!currentUser || !isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
};

// Google Auth Callback Handler
const GoogleAuthCallback = () => {
  const { handleGoogleCallback } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Extract the code from URL search params
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');

        if (code) {
          await handleGoogleCallback(code);
          navigate('/profile'); // Redirect to profile on success
        } else {
          console.error('No code found in callback URL');
          navigate('/login'); // Redirect to login on error
        }
      } catch (error) {
        console.error('Error handling Google callback:', error);
        navigate('/login'); // Redirect to login on error
      }
    };

    handleCallback();
  }, [handleGoogleCallback, location, navigate]);

  return (
    <div className="flex justify-center items-center h-screen">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="text-center"
      >
        <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-text-light">Completing authentication...</p>
      </motion.div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AuthProvider>
        <PaymentProvider>
          <Router>
            <div className="min-h-screen flex flex-col">
              <Header />
              <main className="flex-1">
                <AnimatePresence mode="wait">
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<HomePage />} />
                    <Route
                      path="/shipping-delivery"
                      element={<ShippingDeliveryPage />}
                    />
                    <Route
                      path="/privacy-policy"
                      element={<PrivacyPolicyPage />}
                    />
                    <Route
                      path="/terms-conditions"
                      element={<TermsConditionsPage />}
                    />
                    <Route
                      path="/cancellation-refund"
                      element={<CancellationRefundPage />}
                    />
                    <Route path="/contact" element={<ContactPage />} />

                    {/* Google Auth Callback Route */}
                    <Route
                      path="/auth/callback"
                      element={<GoogleAuthCallback />}
                    />

                    {/* Legacy routes - redirect to home */}
                    <Route path="/login" element={<Navigate to="/" />} />
                    <Route path="/register" element={<Navigate to="/" />} />
                    <Route path="/subscribe" element={<Navigate to="/" />} />

                    {/* Protected Routes */}
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/flowers"
                      element={
                        <AdminRoute>
                          <AdminFlowerPage />
                        </AdminRoute>
                      }
                    />
                  </Routes>
                </AnimatePresence>
              </main>
              <Footer />
            </div>
          </Router>
        </PaymentProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
